import { Section, SectionHead, Card, ArrowLink } from "../components/ui";
import { Reveal } from "../components/Reveal";
import { BrowserFrame } from "../components/BrowserFrame";
import { CASE_STUDIES } from "../data/work";

/**
 * Selected work, condensed.
 *
 * Two studies, because two is what we can evidence. The full four-part
 * narrative lives on /work; this section carries the pull quote and the
 * outcome, which is what a scanning reader takes away anyway.
 *
 * There is no third card teasing "and many more". A visitor who counts two
 * and is told there are two has learned we are small and honest. A visitor
 * who counts two and is told there are dozens has learned something worse.
 */
export function SelectedWork() {
  return (
    <Section id="work">
      <div className="container">
        <SectionHead
          eyebrow="Selected work"
          title={
            <>
              One system,{" "}
              <span className="text-gradient">still running.</span>
            </>
          }
          lede="Described the way we would describe it to another engineer: the problem, what we decided, and what changed. One entry, because one is what we can evidence."
        />

        <div className="mt-10 space-y-5 sm:mt-14">
          {CASE_STUDIES.map((c, i) => (
            <Reveal key={c.slug} delay={i * 0.08}>
              <Card interactive accent={c.accent} className="overflow-hidden">
                <div
                  className={`grid gap-8 p-6 sm:p-8 lg:gap-12 lg:p-10 ${
                    c.shot ? "lg:grid-cols-[1fr_1fr] lg:items-center" : ""
                  }`}
                >
                  <div>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
                      <span>{c.product}</span>
                      <span className="text-white/20">·</span>
                      <span>{c.sector}</span>
                      <span className="text-white/20">·</span>
                      <span>{c.market}</span>
                    </div>

                    <h3 className="mt-3 font-display text-xl font-bold leading-tight tracking-tight sm:text-2xl lg:text-[1.75rem]">
                      {c.title}
                    </h3>

                    <blockquote className="mt-5 border-l-2 border-emerald-400/50 pl-4 font-display text-base font-medium leading-snug text-white/85 sm:text-lg">
                      {c.pullQuote}
                    </blockquote>

                    <div className="mt-6 space-y-4">
                      <Block label="Problem" body={c.problem} clamp />
                      <Block label="Outcome" body={c.outcome} />
                    </div>

                    <div className="mt-7">
                      <ArrowLink href={`/work#${c.slug}`}>
                        Read the full study
                      </ArrowLink>
                    </div>
                  </div>

                  {c.shot ? (
                    <BrowserFrame
                      src={c.shot.src}
                      alt={c.shot.alt}
                      label={`${c.product} · in production`}
                    />
                  ) : null}
                </div>
              </Card>
            </Reveal>
          ))}
        </div>
      </div>
    </Section>
  );
}

function Block({
  label,
  body,
  clamp,
}: {
  label: string;
  body: string;
  clamp?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/35">
        {label}
      </div>
      <p
        className={`mt-1.5 text-sm leading-relaxed text-white/65 ${
          clamp ? "line-clamp-3" : ""
        }`}
      >
        {body}
      </p>
    </div>
  );
}
